import React, { useEffect, useState } from 'react';
import { useAppDispatch } from '../../../hooks/storeHooks';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import Breadcrumb from '../../navigation/components/Breadcrumb';
import UIButtonToolbar from '../../navigation/components/UIButtonToolbar';
import './Asset.css';
import { fetchWellControlActions, fetchEnabledStatus } from '../AssetDataSlice';
import { useAppSelector } from '../../../hooks/storeHooks';
import { setActiveChartKey } from './../../../features/asset-analysis/TrendGroupLabel/TrendGroupLabelSlice';
import { useSettingUrls } from '../../../hooks/useSettingUrls';
import useConfig from '../../../hooks/useConfig';
import { setIndustryApplicationId } from '../../asset-analysis/AssetAnalysisSlice';
import { fetchInitialStateAsync } from '../../asset-status/AssetStatusSlice';
import HeaderElements from '../../navigation/components/HeaderElements';
import ScanResultBar from '../../navigation/components/ScanResultBar';
import { fetchWellTestId } from '../../../dispatchers/WellTestDispatcher';
import { setSelectedAsset, setSelectedGroupByName } from '../../navigation/AssetGroupsSlice';

export interface UrlParams {
  assetId: string;
  assetGroupName: string;
}

function Asset() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const industryApplicationId = useAppSelector((state) => state.AssetAnalysis.industryApplicationId);
  const wellStatusData = useAppSelector((state) => state.rodliftwellstatus.data);
  const assetGroups = useAppSelector((state) => state.assetGroups?.groups || []);
  const selectedAsset = useAppSelector((state) => state.assetGroups.selectedAsset);

  const { assetId, assetGroupName } = useParams<keyof UrlParams>() as UrlParams;
  const currentURLPath = location.pathname.split('/')[2];
  const { isCloud, enablePumpChecker } = useConfig();
  const { pumpCheckerURL } = useSettingUrls();
  const [scrollY, setScrollY] = useState(0);

  const ChartsTabIds = [3, 4, 5, 6, 7, 16, 18];

  useEffect(() => {
    dispatch(fetchWellControlActions(assetId));
    dispatch(fetchEnabledStatus(assetId));
  }, [assetId]);

  useEffect(() => {
    const fetchData = () => {
      dispatch(fetchInitialStateAsync({ assetId: assetId }));
    };
    fetchData();
    const intervalId = setInterval(fetchData, 300000); // 5min
    return () => clearInterval(intervalId);
  }, [assetId]);

  useEffect(() => {
    if (assetGroupName && assetId && assetGroups.length > 0) {
      const decodedGroupName = decodeURIComponent(assetGroupName);
      const decodedAssetId = decodeURIComponent(assetId);

      dispatch(setSelectedGroupByName({ groupName: decodedGroupName }));
      dispatch(setSelectedAsset({ assetId: decodedAssetId }));
    }
  }, [assetGroupName, assetId, assetGroups, dispatch]);

  useEffect(() => {
    if (selectedAsset && selectedAsset.industryApplicationId) {
      dispatch(
        setIndustryApplicationId({
          industryApplicationId: selectedAsset.industryApplicationId,
          currentTab: currentURLPath,
        }),
      );
    }
  }, [selectedAsset, currentURLPath, dispatch]);

  useEffect(() => {
    // Handle scroll events to track vertical scroll position
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setScrollY(scrollTop);
      console.log(scrollY);
    };
    window.addEventListener('scroll', handleScroll);

    // Cleanup: Remove scroll event listener when component unmounts
    // This prevents memory leaks and ensures proper cleanup
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const smartenUrlName = 'Live View';

  useEffect(() => {
    if (assetId) {
      dispatch(fetchWellTestId(assetId));
    }
  }, [assetId]);

  const navButtons = [
    {
      name: 'Status',
      path: 'Status',
      active: currentURLPath === 'status',
      id: 0,
    },
    /* 73702: Disable analysis tab for all lift Types
    {
      name: !isCloud && industryApplicationId === 4 ? 'History Graph' : 'Analysis',
      path: 'Analysis',
      active: currentURLPath === 'analysis',
      id: 1,
    },
    */
    ...(typeof industryApplicationId === 'number' && ChartsTabIds.includes(industryApplicationId)
      ? [
          {
            name: 'Charts',
            path: 'Charts',
            active: currentURLPath === 'charts',
            id: 4,
          },
        ]
      : []),
    {
      name: 'Downtime',
      path: 'Downtime',
      active: currentURLPath === 'downtime',
      id: 12,
    },
    {
      name: 'Comments',
      path: 'comments',
      active: currentURLPath === 'comments',
      id: 14,
    },
    {
      name: 'Tasks',
      path: 'notesAndTasks',
      active: currentURLPath === 'notesandtasks',
      id: 13,
    },

    ...(industryApplicationId === 7 || industryApplicationId === 4 || industryApplicationId === 3
      ? [
          {
            name: 'Alarms',
            path: 'alarms',
            active: currentURLPath === 'alarms',
            id: 6,
          },
        ]
      : []),
    ...(industryApplicationId === 7 || industryApplicationId === 4 || industryApplicationId === 3
      ? [
          {
            name: 'System log',
            path: 'tasks',
            active: currentURLPath === 'tasks',
            id: 7,
          },
        ]
      : []),

    ...(enablePumpChecker && (industryApplicationId === 7 || industryApplicationId === 4)
      ? [
          {
            name: 'Tests',
            path: 'Tests',
            active: currentURLPath === 'tests',
            id: 5,
          },
        ]
      : []),
    ...(enablePumpChecker && industryApplicationId === 4
      ? [
          {
            name: 'Diagnosis',
            path: 'diagnosis',
            active: currentURLPath === 'diagnosis',
            id: 11,
          },
        ]
      : []),
    ...(!isCloud && enablePumpChecker && (industryApplicationId === 7 || industryApplicationId === 4)
      ? [
          {
            name: 'Pump checker diagnosis',
            active: currentURLPath === 'pumpCheckerDiagnosis',
            id: 2,
          },
        ]
      : []),
    ...(wellStatusData?.smartenLiveUrl
      ? [
          {
            name: smartenUrlName,
            id: 3,
            active: false,
          },
        ]
      : []),
    ...(typeof industryApplicationId === 'number' && ChartsTabIds.includes(industryApplicationId)
      ? [
          {
            name: 'Setpoints',
            path: 'setpoints-manager',
            active: currentURLPath === 'setpoints-manager',
            id: 8,
          },
        ]
      : []),
    ...(enablePumpChecker && (industryApplicationId === 7 || industryApplicationId === 4)
      ? [
          {
            name: 'Installs',
            path: 'installs',
            active: currentURLPath === 'installs',
            id: 9,
          },
        ]
      : []),
    ...(enablePumpChecker && (industryApplicationId === 7 || industryApplicationId === 4)
      ? [
          {
            name: 'Completions',
            path: 'completions',
            active: currentURLPath === 'completions',
            id: 10,
          },
        ]
      : []),
  ];

  // Memoize supported paths to avoid recalculating on every render
  const supportedPaths = React.useMemo(() => {
    return navButtons.map((btn) => btn.path && btn.path.toLowerCase()).filter(Boolean);
  }, [navButtons]);

  const isCurrentTabSupported = React.useMemo(() => {
    if (!industryApplicationId || navButtons.length === 0) {
      return false;
    }
    return supportedPaths.includes(currentURLPath?.toLowerCase());
  }, [industryApplicationId, navButtons.length, supportedPaths, currentURLPath]);

  useEffect(() => {
    if (assetId && navButtons.length > 0 && industryApplicationId && !isCurrentTabSupported) {
      navigate(`/asset/status/${assetId}/${encodeURIComponent(assetGroupName)}`, { replace: true });
    }
  }, [assetId, assetGroupName, navigate, isCurrentTabSupported, industryApplicationId, navButtons.length]);

  function onNavChange(navButtonId: number) {
    const navButton = navButtons.find((navButton) => navButton.id == navButtonId);
    if (navButton) {
      if (navButton.name === smartenUrlName) {
        window.open(`${wellStatusData?.smartenLiveUrl}`, '_blank');
        return;
      }
      if (navButton.name === 'Pump checker diagnosis') {
        window.open(`${pumpCheckerURL}/${assetId}`, '_blank');
      } else {
        dispatch(
          setIndustryApplicationId({
            industryApplicationId,
            currentTab: navButton.path?.toLowerCase(),
          }),
        );
        navigate(`/asset/${navButton.path?.toLowerCase()}/${assetId}/${encodeURIComponent(assetGroupName)}`);
      }
    }
    dispatch(setActiveChartKey(1));
  }

  return (
    <div className='flex h-full flex-column chart-data'>
      <div className={`flex flex-row flex-grow asset-nav-bar fix-nav-bar`}>
        {assetId && (
          <div style={{ fontSize: 'large', flexGrow: 1 }}>
            <Breadcrumb />
          </div>
        )}
        <div>
          <HeaderElements showWellsTab={true} showWellSpecificHeader={true} />
        </div>
      </div>
      {assetId ? (
        <div className='flex h-full flex-column'>
          <div className={`asset-ui-buttons-container fix-ui-buttons`}>
            <UIButtonToolbar uiButtons={navButtons} buttonClicked={onNavChange} />
          </div>
          {isCurrentTabSupported && <Outlet />}
          <ScanResultBar />
        </div>
      ) : (
        <div>Bad Route</div>
      )}
    </div>
  );
}

export default Asset;
